import { ReactNode, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

interface PageTransitionProps {
  children: ReactNode
}

/**
 * PageTransition — wraps page content with a subtle fade+slide animation.
 * Triggers on route change via useLocation.
 *
 * Design principle from frontend-slides skill:
 * "One well-orchestrated page-load with staggered reveals creates more
 *  delight than scattered micro-interactions."
 */
export default function PageTransition({ children }: PageTransitionProps) {
  const location = useLocation()
  const [displayChildren, setDisplayChildren] = useState(children)
  const [transitionStage, setTransitionStage] = useState<'enter' | 'active'>('active')

  useEffect(() => {
    // On route change: fade out, swap children, fade in
    setTransitionStage('enter')

    const timeout = setTimeout(() => {
      setDisplayChildren(children)
      setTransitionStage('active')
    }, 150)

    return () => clearTimeout(timeout)
  }, [location.pathname])

  // IMPORTANT: fade with opacity ONLY — no `transform`. Pages use PageLayout,
  // whose full-screen container is `position: fixed`. Any non-`none` transform on
  // an ancestor becomes the containing block for fixed descendants, which collapses
  // that container (main shrinks to a sliver, leaving a big empty area) and unpins
  // fixed bars/modals. Opacity creates only a stacking context, not a containing
  // block, so it is safe here.
  return (
    <div
      className="transition-opacity duration-200 ease-out"
      style={{
        opacity: transitionStage === 'active' ? 1 : 0,
      }}
    >
      {displayChildren}
    </div>
  )
}
